import { Concierge } from '@/types/concierge'
import Loader from '@components/Loader'
import { ConciergeContext } from '@hooks/useConcierge'
import { useConciergeList } from '@hooks/useConciergeList'
import CreateConciergePage from '@pages/CreateConciergePage'
import { doc, getFirestore, onSnapshot, updateDoc } from 'firebase/firestore'
import { ReactNode, useEffect, useState } from 'react'

interface ConciergeProviderProps {
  children: ReactNode
}

const CONCIERGES_COLLECTION = 'concierges'

export function ConciergeProvider({ children }: ConciergeProviderProps) {
  const { selectedConcierge } = useConciergeList()
  const [concierge, setConcierge] = useState<Concierge | null>(null)
  const [loading, setLoading] = useState(true)
  const db = getFirestore()

  useEffect(() => {
    if (!selectedConcierge?.id) {
      setLoading(false)
      return
    }

    let unsubscribe: () => void

    const docRef = doc(db, CONCIERGES_COLLECTION, selectedConcierge.id)

    unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        setConcierge({
          id: docSnapshot.id,
          ...docSnapshot.data(),
          useIframePreview: docSnapshot.data().useIframePreview || false,
        } as Concierge)
      } else {
        console.error('Snapshot does not exist')
      }
      setLoading(false)
    })

    return function cleanup() {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [selectedConcierge?.id, db])

  if (loading) {
    return <Loader />
  }

  if (!selectedConcierge) {
    return <CreateConciergePage />
  }

  if (!concierge) {
    return <Loader />
  }

  async function updateConcierge(updates: Partial<Concierge>) {
    if (!concierge?.id) return

    const docRef = doc(db, CONCIERGES_COLLECTION, concierge.id)
    await updateDoc(docRef, updates)
  }

  return (
    <ConciergeContext.Provider
      value={{
        concierge: concierge,
        updateConcierge,
      }}
    >
      {children}
    </ConciergeContext.Provider>
  )
}
